import {getSetting} from '@woocommerce/settings';
import {registerPaymentMethod, registerExpressPaymentMethod} from '@woocommerce/blocks-registry';
import {isCartPage, isCheckoutPage, isAdminOnly} from "../../utils";
import {loadPayPalSdk, isProduction} from "@ppcp/utils";
import GooglePayPreview from './GooglePayPreview';
import GooglePayExpress from "./GooglePayExpress";
import GooglePay from "./GooglePay";
import {PaymentMethodLabel} from "../../components";

const data = getSetting('ppcp_googlepay_data');
const queryParams = getSetting('paypalQueryParams');

let cachedCanMakePayment = null;
let paymentsClient = null;

/**
 * Check if Google Pay is available and ready to pay
 */
const canMakePayment = async ({cart}) => {
    try {
        const {cartTotals} = cart;
        if (cartTotals.currency_code) {
            if (!data.supportedCurrencies.includes(cartTotals.currency_code)) {
                return false;
            }
        }
        // Check if not admin
        if (isAdminOnly()) {
            return false;
        }

        // Return cached result if available
        if (cachedCanMakePayment !== null) {
            return cachedCanMakePayment;
        }

        // Load PayPal SDK
        const paypal = await loadPayPalSdk(queryParams);

        if (!paypal?.Googlepay) {
            cachedCanMakePayment = false;
            return false;
        }

        // Get Google Pay configuration
        const config = await paypal.Googlepay().config();

        // Create payments client if not already created
        if (!paymentsClient) {
            const {merchantInfo} = config;
            paymentsClient = new google.payments.api.PaymentsClient({
                environment: isProduction() ? 'PRODUCTION' : 'TEST',
                merchantInfo,
            });
        }

        // Check if ready to pay
        const {apiVersion, apiVersionMinor, allowedPaymentMethods} = config;
        const response = await paymentsClient.isReadyToPay({
            apiVersion,
            apiVersionMinor,
            allowedPaymentMethods,
            existingPaymentMethodRequired: false
        });

        cachedCanMakePayment = !!response.result;
        return cachedCanMakePayment;
    } catch (error) {
        console.error('Google Pay canMakePayment check failed:', error);
        cachedCanMakePayment = false;
        return false;
    }
};

registerExpressPaymentMethod({
    name: 'ppcp_googlepay_express',
    gatewayId: 'ppcp_googlepay',
    paymentMethodId: 'ppcp_googlepay',
    canMakePayment: async (props) => {
        // Determine which section we're in
        const section = isCartPage() ? 'cart' : 'express_checkout';

        // Check if Google Pay is enabled for this section
        if (!data.sections.includes(section)) {
            return false;
        }

        // Check if Google Pay is available on this browser/device
        return await canMakePayment(props);
    },
    content: <GooglePayExpress paymentMethodId={'ppcp_googlepay_express'}/>,
    edit: <GooglePayPreview src={data.editorIcons.long} buttonType={data.button.buttonType}/>
});

registerPaymentMethod({
    name: 'ppcp_googlepay',
    gatewayId: 'ppcp_googlepay',
    paymentMethodId: 'ppcp_googlepay',
    canMakePayment: async (props) => {
        // Check if Google Pay is enabled for block payment method
        if (!data.sections.includes('checkout')) {
            return false;
        }

        // Check if Google Pay is available on this browser/device
        return await canMakePayment(props);
    },
    content: <GooglePay paymentMethodId={'ppcp_googlepay'}/>,
    edit: <GooglePayPreview src={data.editorIcons.long} buttonType={data.button.buttonType}/>,
    ariaLabel: 'Google Pay',
    label: <PaymentMethodLabel id={'ppcp_googlepay'} title={data.title} icons={data.icons}/>
});